/**
 * Pricing agent reference tables.
 *
 * PLACEHOLDER / ILLUSTRATIVE DATA. Classified INTERNAL.
 *
 * Nothing existed for this agent in Partnership Net 2.0 beyond a catalogue
 * card, so the reference data here was built from the agent's own brief:
 * top-down from a short question set, or bottom-up at rights level with the
 * method library, producing a defensible range with the workings shown.
 *
 * Comparables are anonymised. The live agent reads them from the benchmarking
 * database, which stays internal and is never shared with a client.
 */

import type { PricingReference } from '../../contracts';

export const PRICING_REFERENCE: PricingReference = {
  categories: [
    {
      id: 'football-t1',
      label: 'Football — Tier 1 club',
      cpm: 16.2,
      fanMult: 0.62,
      evRev: 0.85,
      scarcity: 1.1,
    },
    {
      id: 'football-t2',
      label: 'Football — Tier 2 club',
      cpm: 12.4,
      fanMult: 0.48,
      evRev: 0.72,
      scarcity: 1.0,
    },
    {
      id: 'league',
      label: 'League / competition',
      cpm: 18.6,
      fanMult: 0.7,
      evRev: 0.94,
      scarcity: 1.18,
    },
    {
      id: 'motorsport',
      label: 'Motorsport team',
      cpm: 15.1,
      fanMult: 0.58,
      evRev: 0.81,
      scarcity: 1.12,
    },
    {
      id: 'cycling',
      label: 'Cycling WorldTour team',
      cpm: 9.8,
      fanMult: 0.34,
      evRev: 0.61,
      scarcity: 0.94,
    },
    {
      id: 'basketball',
      label: 'Basketball franchise',
      cpm: 14.3,
      fanMult: 0.55,
      evRev: 0.78,
      scarcity: 1.05,
    },
    {
      id: 'athlete',
      label: 'Individual athlete',
      cpm: 11.6,
      fanMult: 0.41,
      evRev: 0.55,
      scarcity: 0.98,
    },
    {
      id: 'esports',
      label: 'Esports organisation',
      cpm: 8.4,
      fanMult: 0.29,
      evRev: 0.48,
      scarcity: 0.88,
    },
  ],
  methods: [
    {
      id: 'cpm',
      label: 'Media equivalence (CPM)',
      unit: 'impressions (M)',
      hint: 'Impressions × CPM, discounted for clutter',
    },
    {
      id: 'replace',
      label: 'Cost replacement',
      unit: 'units',
      hint: 'What buying the same outcome elsewhere would cost',
    },
    {
      id: 'comparable',
      label: 'Comparable deal',
      unit: 'index (100 = comp)',
      hint: 'Comparable transaction, indexed to this asset',
    },
    {
      id: 'attention',
      label: 'Attention share',
      unit: 'attention-seconds (M)',
      hint: 'Attention-weighted exposure priced per second',
    },
    {
      id: 'commercial',
      label: 'Commercial yield',
      unit: 'EUR of tracked revenue',
      hint: 'Directly attributable revenue, margin adjusted',
    },
  ],
  rightsLibrary: [
    {
      id: 'jersey-front',
      label: 'Front-of-jersey',
      method: 'cpm',
      qty: 3200,
      rate: 16.2,
      disc: 0.62,
    },
    { id: 'jersey-sleeve', label: 'Sleeve', method: 'cpm', qty: 1400, rate: 16.2, disc: 0.55 },
    { id: 'led', label: 'Stadium LED perimeter', method: 'cpm', qty: 1800, rate: 12.8, disc: 0.48 },
    {
      id: 'static',
      label: 'Stadium static signage',
      method: 'cpm',
      qty: 700,
      rate: 9.4,
      disc: 0.42,
    },
    {
      id: 'naming',
      label: 'Venue naming rights',
      method: 'comparable',
      qty: 100,
      rate: 4200000,
      disc: 1.0,
    },
    {
      id: 'hospitality',
      label: 'Hospitality allocation',
      method: 'replace',
      qty: 640,
      rate: 820,
      disc: 1.0,
    },
    {
      id: 'content',
      label: 'Content & player access',
      method: 'attention',
      qty: 34,
      rate: 38000,
      disc: 1.0,
    },
    {
      id: 'ip-licence',
      label: 'IP & marks licence',
      method: 'comparable',
      qty: 100,
      rate: 960000,
      disc: 1.0,
    },
    {
      id: 'data',
      label: 'Fan data & CRM access',
      method: 'commercial',
      qty: 1850000,
      rate: 0.14,
      disc: 1.0,
    },
    {
      id: 'retail',
      label: 'Retail & product rights',
      method: 'commercial',
      qty: 4200000,
      rate: 0.09,
      disc: 1.0,
    },
  ],
  adjustments: [
    {
      id: 'exclusivity',
      label: 'Category exclusivity',
      pct: 12,
      note: 'Premium for locking the category',
    },
    { id: 'term', label: 'Multi-year term (3 yr+)', pct: -8, note: 'Discount for committed term' },
    {
      id: 'scarcity',
      label: 'Category scarcity',
      pct: 0,
      note: 'Set from the category reference table',
    },
    {
      id: 'activation',
      label: 'Activation obligations on the rights holder',
      pct: -6,
      note: 'Discount where the holder carries delivery cost',
    },
    {
      id: 'performance',
      label: 'Performance risk (sporting outcome)',
      pct: -5,
      note: 'Discount for outcome-dependent value',
    },
    {
      id: 'renewal',
      label: 'Incumbent renewal',
      pct: -4,
      note: 'Discount reflecting no pitch and switching cost',
    },
  ],
  comparables: [
    {
      deal: 'Comparable A — Tier 1 club, front-of-jersey',
      fee: 40000000,
      reach: '92M followers',
      cpm: 15.4,
    },
    {
      deal: 'Comparable B — Tier 1 club, front-of-jersey',
      fee: 47000000,
      reach: '118M followers',
      cpm: 16.8,
    },
    {
      deal: 'Comparable C — Tier 1 club, front-of-jersey',
      fee: 43000000,
      reach: '104M followers',
      cpm: 15.9,
    },
    { deal: 'Comparable D — League title partner', fee: 56000000, reach: '210M reach', cpm: 18.2 },
    {
      deal: 'Comparable E — Tier 2 club, front-of-jersey',
      fee: 18000000,
      reach: '41M followers',
      cpm: 12.1,
    },
  ],
  /** [id, label, index] — the share of property value this asset tier carries. */
  tiers: [
    ['title', 'Title / naming partner', 1.0],
    ['front', 'Front-of-jersey / primary', 0.72],
    ['secondary', 'Secondary / sleeve / back', 0.34],
    ['supplier', 'Official supplier / category', 0.18],
  ],
  /** [id, label, retained fraction] — how much value survives the clutter. */
  clutter: [
    ['low', 'Low — few competing marks', 0.72],
    ['medium', 'Medium — typical inventory', 0.58],
    ['high', 'High — crowded asset set', 0.42],
  ],
  /**
   * CPM and attention carry the widest error bars because both rest on modelled
   * exposure; commercial yield is narrowest because it is tracked revenue.
   */
  methodUncertainty: {
    cpm: 0.28,
    attention: 0.3,
    comparable: 0.18,
    replace: 0.12,
    commercial: 0.1,
  },
  defaultUncertainty: 0.2,
};
